import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  MoreHorizontal,
  Edit,
  Pause,
  Play,
  Trash2,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

// Safe date formatting function that handles invalid dates
const safeFormat = (dateString: string | Date | undefined | null, formatString: string, fallback: string = "Data inválida") => {
  try {
    // Handle null, undefined, or empty string
    if (dateString === null || dateString === undefined || dateString === '') {
      return fallback;
    }

    // Convert Date object to string if needed
    const dateStr = dateString instanceof Date ? dateString.toISOString() : String(dateString);

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return fallback;
    }

    return format(date, formatString, { locale: ptBR });
  } catch (error) {
    console.error('Error formatting date in RecurringTransactionsList:', dateString, error);
    return fallback;
  }
};

interface RecurringTransaction {
  id: string;
  description: string;
  amount: number;
  type: "income" | "expense";
  category: string;
  pattern?: {
    type: "daily" | "weekly" | "monthly" | "yearly";
    interval: number;
    monthlyDay?: number;
    weeklyDay?: number;
    yearlyMonth?: number;
    yearlyDay?: number;
  };
  next_date?: Date | string | null;
  date: Date | string;
  recurrence_pattern?: "daily" | "weekly" | "monthly" | "yearly";
  recurrence_interval?: number;
  recurrence_end_date?: Date | string | null;
  recurrence_count?: number | null;
  recurrence_created_count?: number;
  max_occurrences?: number | null;
  current_occurrences?: number;
  is_active?: boolean;
  remaining_occurrences?: number | null;
}

interface RecurringTransactionsListProps {
  transactions: RecurringTransaction[];
  onEdit: (transaction: RecurringTransaction) => void;
  onPause: (transactionId: string) => Promise<void>;
  onDelete: (transactionId: string) => Promise<void>;
}

const patternTypeLabels = {
  daily: "Diário",
  weekly: "Semanal",
  monthly: "Mensal",
  yearly: "Anual"
};

const patternDescriptions = {
  daily: (interval: number) => interval === 1 ? "Todo dia" : `A cada ${interval} dias`,
  weekly: (interval: number, day?: number) => {
    const days = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
    const dayName = day !== undefined ? days[day] : "";
    return interval === 1 ? `Toda ${dayName.toLowerCase()}` : `A cada ${interval} semanas na ${dayName}`;
  },
  monthly: (interval: number, day?: number) => {
    const dayNum = day || 1;
    return interval === 1 ? `Todo dia ${dayNum}` : `A cada ${interval} meses no dia ${dayNum}`;
  },
  yearly: (interval: number, month?: number, day?: number) => {
    const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const monthName = month !== undefined ? months[month - 1] : "";
    const dayNum = day || 1;
    return interval === 1 ? `Todo dia ${dayNum} de ${monthName}` : `A cada ${interval} anos em ${monthName}`;
  }
};

export function RecurringTransactionsList({
  transactions,
  onEdit,
  onPause,
  onDelete
}: RecurringTransactionsListProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  const handlePauseResume = async (transactionId: string) => {
    setLoading(transactionId);
    try {
      await onPause(transactionId);
    } finally {
      setLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedTransactionId) return;

    setLoading(selectedTransactionId);
    try {
      await onDelete(selectedTransactionId);
      setShowDeleteConfirm(false);
      setSelectedTransactionId(null);
    } finally {
      setLoading(null);
    }
  };

  const initiateDelete = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setShowDeleteConfirm(true);
  };

  const getPatternDescription = (transaction: RecurringTransaction) => {
    const type = transaction.recurrence_pattern || transaction.pattern?.type || "monthly";
    const interval = transaction.recurrence_interval || transaction.pattern?.interval || 1;

    switch (type) {
      case "daily":
        return patternDescriptions.daily(interval);
      case "weekly":
        return patternDescriptions.weekly(interval, transaction.pattern?.weeklyDay);
      case "monthly":
        return patternDescriptions.monthly(interval, transaction.pattern?.monthlyDay);
      case "yearly":
        return patternDescriptions.yearly(interval, transaction.pattern?.yearlyMonth, transaction.pattern?.yearlyDay);
      default:
        return patternTypeLabels[type as keyof typeof patternTypeLabels];
    }
  };

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">Nenhuma transação recorrente encontrada</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Crie sua primeira transação recorrente para automatizar seus pagamentos
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {transactions.map((transaction) => (
          <Card key={transaction.id} className="card-elevated hover:shadow-lg hover:border-primary/30 transition-all duration-200">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium truncate">
                      {transaction.description}
                    </h3>
                    <Badge
                      variant="outline"
                      className={
                        transaction.type === "income"
                          ? "border-green-600 text-green-600 hover:bg-green-50"
                          : "border-red-600 text-red-600 hover:bg-red-50"
                      }
                    >
                      {transaction.type === "income" ? "Receita" : "Despesa"}
                    </Badge>
                    <Badge variant={transaction.is_active ? "default" : "secondary"}>
                      {transaction.is_active ? "Ativa" : "Pausada"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {transaction.category}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span className={`text-lg font-bold ${
                    transaction.type === "income" ? "text-green-600" : "text-red-600"
                  }`}>
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                      signDisplay: "always"
                    }).format(transaction.amount)}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Abrir menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Ações</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => onEdit(transaction)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handlePauseResume(transaction.id)}
                        disabled={loading === transaction.id}
                      >
                        {transaction.is_active ? (
                          <>
                            <Pause className="h-4 w-4 mr-2" />
                            Pausar
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Ativar
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => initiateDelete(transaction.id)}
                        className="text-destructive"
                        disabled={loading === transaction.id}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between text-sm">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      Próxima: {safeFormat(transaction.next_date || undefined, "dd 'de' MMMM 'de' yyyy", "Sem data")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {transaction.type === "income" ? (
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600" />
                    )}
                    <span className="text-muted-foreground">
                      {getPatternDescription(transaction)}
                    </span>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Ocorrências: {transaction.current_occurrences}
                    {transaction.remaining_occurrences && (
                      <span> / {transaction.remaining_occurrences + transaction.current_occurrences}</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Início: {safeFormat(String(transaction.date), "MM/yyyy", "Sem data")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta transação recorrente?
              Esta ação não pode ser desfeita e também excluirá todas as transações
              futuras geradas a partir desta recorrência.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading === selectedTransactionId}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loading === selectedTransactionId}
              className="bg-destructive hover:bg-destructive/90"
            >
              {loading === selectedTransactionId ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}